import { AxiosPromise } from "axios";
import {
  DisconnectedDrivingFilterInputDto,
  DisconnectedDrivingLocationGroupDto,
  DisconnectedDrivingLocationGroupDtoPagedResultDto,
  FilterInputDto,
  FilterRule,
} from "../../../common/data/service-proxies";
import { ICrudApiProxy } from "../../../common/data/types";

type FilterValues = Record<string, unknown>;

const collectFilterValues = (
  rule: FilterRule | null | undefined,
  result: FilterValues = {}
): FilterValues => {
  if (!rule) {
    return result;
  }

  if (rule.field) {
    result[rule.field] = rule.value;
  }

  rule.filterRules?.forEach((child) => collectFilterValues(child, result));
  return result;
};

const parseOptionalPositiveInteger = (value: unknown): number | undefined => {
  if (value === "" || value === null || value === undefined) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const parseCompanyIds = (value: unknown): number[] => {
  const values = Array.isArray(value) ? value : String(value ?? "").split(",");
  return values
    .map(parseOptionalPositiveInteger)
    .filter((id): id is number => id !== undefined);
};

// Returns undefined for a cleared or unparseable value. Sending an empty string
// instead would hit `[Required] DateTime?` as a JSON conversion failure rather
// than the server's own validation message.
const toLocalCalendarDateTime = (value: unknown): string | undefined => {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value}T00:00:00`;
  }

  const date = value instanceof Date ? value : new Date(String(value ?? ""));
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}T00:00:00`;
};

export const mapDisconnectedDrivingListInput = (
  input?: FilterInputDto
): Omit<DisconnectedDrivingFilterInputDto, "dateFrom" | "dateTo"> & {
  dateFrom?: string;
  dateTo?: string;
} => {
  const filters = collectFilterValues(input?.filterRule);

  return {
    dateFrom: toLocalCalendarDateTime(filters.dateFrom),
    dateTo: toLocalCalendarDateTime(filters.dateTo),
    companyIds: parseCompanyIds(filters.companyIds),
    groupId: parseOptionalPositiveInteger(filters.groupId),
    vehicleId: parseOptionalPositiveInteger(filters.vehicleId),
    driverId: parseOptionalPositiveInteger(filters.driverId),
    sorting: input?.sorting,
    skipCount: Math.max(0, input?.skipCount ?? 0),
    maxResultCount: Math.min(100, Math.max(1, input?.maxResultCount ?? 25)),
  };
};

/**
 * The #5506 contract returns one row per disconnected-driving *segment*, grouped by
 * vehicle, local day, driver and segment number. A new segment starts on a driver
 * change, a gap over 60 minutes, or an intervening non-disconnected location, so
 * several rows legitimately share a vehicle-day and the identifier must include the
 * segment start.
 */
export const withDisconnectedDrivingIds = (
  response: DisconnectedDrivingLocationGroupDtoPagedResultDto
) => ({
  ...response,
  items: response.items?.map((item: DisconnectedDrivingLocationGroupDto) => ({
    ...item,
    id: `${item.vehicleId ?? "vehicle"}:${item.date ?? "date"}:${
      item.driverId ?? "unassigned"
    }:${item.startTime ?? "start"}`,
  })),
});

export const DisconnectedDrivingApiFactoryAdapter = () => ({
  get(target: ICrudApiProxy, prop: string) {
    if (prop !== "apiAppDisconnecteddrivingGetFilteredListPost") {
      return target[prop];
    }

    return (
      input?: FilterInputDto,
      options?: unknown
    ): AxiosPromise<DisconnectedDrivingLocationGroupDtoPagedResultDto> =>
      (target[prop] as Function)(
        mapDisconnectedDrivingListInput(input),
        options
      ).then((response: any) => ({
        ...response,
        data: withDisconnectedDrivingIds(response.data),
      }));
  },
});
